---
title: "What Is Future-Dated Publishing?"
slug: "future-post-demo"
excerpt: "A scheduled article reference used to verify that future posts stay hidden until publish date."
publishedAt: "2026-06-10"
updatedAt: "2026-06-10"
author: "maya-lestari"
category: "Publishing"
tags:
  - scheduling
  - future posts
  - workflow
featuredImage: "/images/future-schedule.svg"
status: "published"
draft: false
---

## Short Answer

<ShortAnswer>
Future-dated publishing schedules an article for a later date so it can be previewed in development while staying hidden from public pages, search, related posts, and sitemaps.
</ShortAnswer>

## Infobox

<InfoBox title="Future-dated publishing">
<ul>
<li><strong>Status:</strong> scheduled until the publish date arrives.</li>
<li><strong>Preview route:</strong> add `?preview=true` during local development.</li>
<li><strong>Hidden from:</strong> home, archives, search, related posts, sitemap, and prerendered public routes.</li>
<li><strong>Visibility rule:</strong> published status, not draft, and publish date is not in the future.</li>
</ul>
</InfoBox>

## Overview

This article is intentionally dated in the future. It should not appear on public navigation until its publish date, but editors still need a way to inspect layout, schema, and article content before release.

Preview mode solves that by allowing local development access without changing production visibility.

## History / Background

Scheduled publishing is common in editorial systems because teams often prepare articles before launch. Static sites need an explicit visibility check because future content can otherwise be bundled into archives or sitemap output too early.

This project uses date-aware filtering so scheduled posts stay private in generated public pages.

## How It Works

The `isPublished(post)` helper checks that the post is not a draft, has published status, and does not have a future `publishedAt` date.

During local development, preview mode can bypass that public visibility rule for the direct article route. Production prerendering still excludes future-dated routes because the static route list only uses currently published posts.

## Important Facts

<FactList>
<ul>
<li>Future posts should not appear in public article lists before their date.</li>
<li>Preview mode should be limited to development builds.</li>
<li>Sitemap generation must use the same publication rules as public pages.</li>
<li>Direct route previews help editors check layout and metadata before release.</li>
</ul>
</FactList>

## Timeline

<Timeline items={[
  { date: "Draft", title: "Article is prepared", description: "Editors write and review the page before public release." },
  { date: "Scheduled", title: "Publish date is set", description: "The page has published status but a future date." },
  { date: "Preview", title: "Local route is checked", description: "Editors use preview mode to inspect the page during development." },
  { date: "Published", title: "Date arrives", description: "The article becomes eligible for lists, related posts, sitemap, and prerendering." }
]} />

## Related Terms

<RelatedTerms>
<ul>
<li>Draft</li>
<li>Preview mode</li>
<li>Prerendering</li>
<li>Sitemap</li>
<li>Editorial workflow</li>
</ul>
</RelatedTerms>

## FAQ

<FAQ>
<details>
<summary>Can visitors see this page before the publish date?</summary>
<p>No. Public routes reject future-dated posts unless development preview mode is enabled.</p>
</details>
<details>
<summary>Why not rely only on draft status?</summary>
<p>Draft status and scheduling solve different problems. A scheduled article may be editorially ready but intentionally delayed.</p>
</details>
<details>
<summary>Where should future posts be excluded?</summary>
<p>They should be excluded from home pages, taxonomy pages, author archives, search results, related posts, sitemaps, and production prerender routes.</p>
</details>
</FAQ>

## References

<ReferenceList>
<ol>
<li>Project publication helper `isPublished(post)`.</li>
<li>Static route generation in the prerender workflow.</li>
<li>Common editorial scheduling patterns in content management systems.</li>
</ol>
</ReferenceList>
